var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import React, { useEffect, useRef, useState } from 'react';
typeof window !== 'undefined' && !window.__DS_WEB_VERSION__ && (window.__DS_WEB_VERSION__ = '2.10.0');
import { useUniqueId } from '../../hooks/useUniqueId';
import { Options, Chips, Button, Filter, IconLeft, IconRight } from './components';
import { handleOptionsNavigation, optionsNavigation } from './utils';
import { useClickOutside } from './hooks';
import * as S from './styles';
import Label from './components/Label';
import { validateAccessibilityProps, validateLabelAndIdProps } from './utils/acessibilityErrors';
import { handleFilteredItems } from './utils/handleFilteredItems';
/**
 * Componente Multiselect do Design System OLX
 */
export const Multiselect = _a => {
  var {
      items,
      onChange,
      placeholder,
      iconLeft,
      label,
      id,
      state,
      stateMessage,
      selectedItems = [],
      sizeMaxOverflow,
      caption,
      showList,
      clearInput = false,
      autoFocus = false,
      alwaysOpen = false,
      noMatchMessage = 'Nenhum resultado encontrado',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      onTextChange = () => null
    } = _a,
    props = __rest(_a, ["items", "onChange", "placeholder", "iconLeft", "label", "id", "state", "stateMessage", "selectedItems", "sizeMaxOverflow", "caption", "showList", "clearInput", "autoFocus", "alwaysOpen", "noMatchMessage", 'aria-label', 'aria-labelledby', "onTextChange"]);
  const initialDropdownState = showList || alwaysOpen ? "open" /* DropdownStates.OPEN */ : "closed" /* DropdownStates.CLOSED */;
  const openedStates = ["open" /* DropdownStates.OPEN */, "editing" /* DropdownStates.EDITING */];
  const [dropdownState, setDropdownState] = useState(initialDropdownState);
  const wrapperRef = useRef(null);
  const isOpen = openedStates.includes(dropdownState);
  useClickOutside({
    wrapperRef,
    isOpen,
    setDropdownState,
    showList
  });
  const [filterTerm, setFilterTerm] = useState('');
  const searchRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  useEffect(() => {
    if (dropdownState === "editing" /* DropdownStates.EDITING */) {
      setTimeout(() => {
        var _a;
        return (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
      }, 100);
    }
    if (dropdownState === "open" /* DropdownStates.OPEN */ && autoFocus) {
      setDropdownState("editing" /* DropdownStates.EDITING */);
    }
  }, [dropdownState, autoFocus]);
  useEffect(() => {
    clearInput && setFilterTerm('');
  }, [clearInput]);
  const handleCheckboxChange = item => {
    if (!selectedItems.includes(item)) {
      onChange([...selectedItems, item]);
    } else {
      onChange(selectedItems.filter(selected => selected !== item));
    }
    setDropdownState("open" /* DropdownStates.OPEN */);
  };
  const filteredItems = handleFilteredItems(items, filterTerm);
  const optionsID = useUniqueId('ds-select-options');
  const handleClickChip = item => {
    onChange(selectedItems.filter(selected => selected !== item));
    setDropdownState("open" /* DropdownStates.OPEN */);
  };
  const handleClickButton = () => {
    var _a;
    if (dropdownState === "open" /* DropdownStates.OPEN */) {
      setDropdownState("editing" /* DropdownStates.EDITING */);
      setFilterTerm('');
      (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    } else if (dropdownState === "editing" /* DropdownStates.EDITING */ && !alwaysOpen) {
      setDropdownState("closed" /* DropdownStates.CLOSED */);
    } else {
      setDropdownState("open" /* DropdownStates.OPEN */);
    }
  };
  const showDropdownList = isOpen && !state && !!items.length;
  const contentClass = [state === 'error' && 'has-error', state === 'disabled' && 'has-disabled', selectedItems.length && 'is-selected', iconLeft && 'has-icon-left', selectedItems.length && isOpen && 'is-selected-open'].filter(Boolean).join(' ');
  const isEmpty = isOpen && filterTerm && !filteredItems.length;
  const isEmptyMessage = isEmpty && noMatchMessage;
  const isError = state === 'error';
  const isErrorMessage = isError && stateMessage;
  const messageHelpText = isEmptyMessage || isErrorMessage || caption;
  validateAccessibilityProps({
    ariaLabel,
    ariaLabelledBy,
    state,
    stateMessage,
    label
  });
  validateLabelAndIdProps({
    label,
    id
  });
  const onTextChangeHandler = value => {
    setFilterTerm(value);
    onTextChange === null || onTextChange === void 0 ? void 0 : onTextChange(value);
  };
  return React.createElement(S.Wrapper, Object.assign({
    "data-ds-component": "DS-Multiselect"
  }, props), React.createElement(Label, {
    id: id
  }, label), React.createElement(S.Content, {
    ref: wrapperRef,
    className: contentClass
  }, React.createElement(IconLeft, {
    className: contentClass
  }, iconLeft), React.createElement(Button, {
    id: id,
    onClick: handleClickButton,
    "aria-expanded": isOpen,
    onKeyDown: e => handleOptionsNavigation({
      e,
      wrapperRef,
      isOpen,
      setDropdownState
    }),
    disabled: state === 'disabled',
    className: contentClass,
    ref: buttonRef
  }, placeholder && !(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) && !isOpen && React.createElement(React.Fragment, null, placeholder)), React.createElement(Chips, {
    selectedItems: selectedItems,
    items: items,
    onClick: item => handleClickChip(item),
    isOpen: isOpen,
    className: contentClass
  }, state !== 'error' && state !== 'loading' && isOpen && React.createElement(Filter, Object.assign({}, ariaLabel && {
    'aria-label': ariaLabel
  }, !ariaLabel && {
    'aria-labelledby': id
  }, {
    "aria-expanded": isOpen,
    "aria-controls": optionsID,
    ref: searchRef,
    value: filterTerm,
    onChange: e => onTextChangeHandler(e.target.value),
    placeholder: !(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) ? placeholder : '',
    onKeyDown: e => handleOptionsNavigation({
      e,
      wrapperRef,
      isOpen,
      setDropdownState,
      buttonRef
    })
  }))), !alwaysOpen && React.createElement(IconRight, {
    state: state,
    isOpen: showDropdownList,
    "aria-label": label || ariaLabel
  }), React.createElement(Options, {
    id: optionsID,
    showDropdownList: showDropdownList,
    filteredItems: filteredItems,
    selectedItems: selectedItems,
    handleCheckboxChange: handleCheckboxChange,
    isOpen: isOpen,
    sizeMaxOverflow: sizeMaxOverflow,
    onKeyDown: event => optionsNavigation({
      isOpen,
      wrapperRef,
      event,
      setDropdownState,
      buttonRef
    })
  })), React.createElement(S.HelpText, {
    isError: isError
  }, messageHelpText));
};
Multiselect.displayName = 'Multiselect';